// The Sun as a red giant, for the far future's sky (?whatif=future, episode 6
// of the Voidmode Studios channel). futureState.sunScale above GIANT_FROM
// swaps the Sun for this: a sphere in the scaled scene (createRedGiant, seen
// from space) and the same surface drawn by the close sky dome when it is
// seen from the ground (src/eclipse/totality.js, uGiant), where it can fill
// more than half the sky.
//
// The look. Schroeder and Smith (2008, MNRAS 386, 155) put the Sun at the
// tip of the red giant branch at 256 solar radii and 2,730 solar
// luminosities, which is an effective temperature of 5,772 K x (2,730 /
// 256^2)^(1/4) = 2,600 K: a deep orange disc whose limb, cooler still and
// darkened strongly, runs to red. Its convection is a few cells across the
// whole star, as interferometry shows on the giant pi1 Gruis (Paladini et
// al. 2018, Nature 553, 310: granules about 1.2e11 m on a star of about 350
// solar radii, a quarter to a half of its radius), so the disc shows about
// ten cells across, each with finer churning inside it and dark cool lanes
// between, boiling slowly (a time-lapse: the real ones last months).

import * as THREE from 'three';

// Below this the Sun keeps its own meshes; the swelling to a giant is not
// something a shot passes through slowly.
export const GIANT_FROM = 3;

// The giant's surface radiance for a unit normal n on the star (in a frame
// fixed to the star) seen at mu (the cosine between the normal and the line
// of sight), with t a clock in seconds. Relative units: about 1 at the
// disc's centre.
export const GIANT_SURFACE_GLSL = /* glsl */ `
  float giantHash1(vec3 p) { return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453); }
  vec3 giantHash3(vec3 p) {
    return fract(sin(vec3(dot(p, vec3(127.1, 311.7, 74.7)), dot(p, vec3(269.5, 183.3, 246.1)), dot(p, vec3(113.5, 271.9, 124.6)))) * 43758.5453);
  }
  // Worley distances to the nearest two feature points, each point
  // wandering on its own slow orbit so cells swell, merge and split.
  vec2 giantCells(vec3 p, float t) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    float d1 = 8.0, d2 = 8.0;
    for (int z = -1; z <= 1; z++)
    for (int y = -1; y <= 1; y++)
    for (int x = -1; x <= 1; x++) {
      vec3 g = vec3(float(x), float(y), float(z));
      vec3 h = giantHash3(i + g);
      vec3 o = 0.5 + 0.38 * sin(t * (0.6 + 0.5 * h) + 6.2831853 * h);
      vec3 r = g + o - f;
      float d = dot(r, r);
      if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) { d2 = d; }
    }
    return sqrt(vec2(d1, d2));
  }
  float giantNoise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    vec3 u = f * f * (3.0 - 2.0 * f);
    float n000 = giantHash1(i), n100 = giantHash1(i + vec3(1, 0, 0));
    float n010 = giantHash1(i + vec3(0, 1, 0)), n110 = giantHash1(i + vec3(1, 1, 0));
    float n001 = giantHash1(i + vec3(0, 0, 1)), n101 = giantHash1(i + vec3(1, 0, 1));
    float n011 = giantHash1(i + vec3(0, 1, 1)), n111 = giantHash1(i + vec3(1, 1, 1));
    return mix(mix(mix(n000, n100, u.x), mix(n010, n110, u.x), u.y), mix(mix(n001, n101, u.x), mix(n011, n111, u.x), u.y), u.z);
  }
  vec3 giantSurface(vec3 n, float mu, float t) {
    float clock = t * 0.05;
    // A slow domain warp keeps the cells from reading as a lattice.
    vec3 w = vec3(giantNoise(n * 2.5 + clock * 0.3), giantNoise(n * 2.5 + 17.0 - clock * 0.25), giantNoise(n * 2.5 + 41.0 + clock * 0.2));
    vec3 q = n * 4.0 + (w - 0.5) * 1.2;
    vec2 c = giantCells(q, clock);
    float lane = smoothstep(0.0, 0.5, c.y - c.x);            // 0 in the cooler lanes between cells
    float rise = 1.0 - smoothstep(0.0, 0.9, c.x);            // brightest where the hot gas rises
    vec2 c2 = giantCells(q * 3.3 + 9.0, clock * 1.6);
    float fine = smoothstep(0.0, 0.4, c2.y - c2.x);
    // Turbulence on every scale below the cells.
    float f = 0.5 * giantNoise(n * 14.0 + clock) + 0.25 * giantNoise(n * 31.0 - clock * 1.3)
      + 0.125 * giantNoise(n * 70.0 + clock * 2.0) + 0.0625 * giantNoise(n * 160.0 - clock * 3.0);
    float cell = (0.78 + 0.22 * lane) * (0.84 + 0.28 * rise) * (0.94 + 0.06 * fine) * (0.88 + 0.24 * f);
    // Strong limb darkening for a cool, extended atmosphere.
    float ld = pow(max(mu, 0.0), 0.75);
    float heat = clamp(cell * (0.55 + 0.45 * ld), 0.0, 1.3);
    // 2,600 K at the centre of a bright cell, near 2,000 K in the lanes
    // and at the limb: orange to deep red.
    vec3 hot = vec3(1.0, 0.58, 0.26);
    vec3 cool = vec3(0.82, 0.22, 0.07);
    vec3 col = mix(cool, hot, smoothstep(0.3, 1.1, heat));
    return col * heat * ld;
  }
`;

const VERT = /* glsl */ `
  varying vec3 vN;
  varying vec3 vWorld;
  varying vec3 vWorldN;
  #include <common>
  #include <logdepthbuf_pars_vertex>
  void main() {
    vN = normalize(position);
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorld = world.xyz;
    vWorldN = normalize(mat3(modelMatrix) * position);
    gl_Position = projectionMatrix * viewMatrix * world;
    #include <logdepthbuf_vertex>
  }
`;

const FRAG = /* glsl */ `
  uniform float uTime;
  uniform float uGain;
  varying vec3 vN;
  varying vec3 vWorld;
  varying vec3 vWorldN;
  #include <common>
  #include <logdepthbuf_pars_fragment>
  ${GIANT_SURFACE_GLSL}
  void main() {
    #include <logdepthbuf_fragment>
    vec3 view = normalize(cameraPosition - vWorld);
    float mu = max(dot(normalize(vWorldN), view), 0.0);
    gl_FragColor = vec4(giantSurface(vN, mu, uTime) * uGain, 1.0);
  }
`;

// The faint glow of the giant's extended atmosphere round its limb, on a
// larger back-facing shell: by how close the line of sight passes the
// centre, in star radii.
const HALO_FRAG = /* glsl */ `
  uniform float uGain;
  uniform vec3 uCenter;
  uniform float uRadius;
  varying vec3 vWorld;
  #include <common>
  #include <logdepthbuf_pars_fragment>
  void main() {
    #include <logdepthbuf_fragment>
    vec3 d = normalize(vWorld - cameraPosition);
    vec3 m = uCenter - cameraPosition;
    float along = dot(m, d);
    float rmin = length(m - d * along) / uRadius;
    float h = rmin > 1.0 ? exp(-(rmin - 1.0) * 9.0) * 0.5 + exp(-(rmin - 1.0) * 2.5) * 0.12 : 0.0;
    gl_FragColor = vec4(vec3(1.0, 0.36, 0.12) * h * uGain, 1.0);
  }
`;

// A sphere of radius 1 (scale it to the star's radius in scene units) with
// its halo. update(time) every frame.
export function createRedGiant() {
  const uniforms = { uTime: { value: 0 }, uGain: { value: 1 } };
  const material = new THREE.ShaderMaterial({ uniforms, vertexShader: VERT, fragmentShader: FRAG });
  const group = new THREE.Group();
  group.name = 'redGiant';
  const star = new THREE.Mesh(new THREE.SphereGeometry(1, 192, 96), material);
  star.frustumCulled = false;
  group.add(star);
  const haloUniforms = { uGain: uniforms.uGain, uCenter: { value: new THREE.Vector3() }, uRadius: { value: 1 } };
  const halo = new THREE.Mesh(new THREE.SphereGeometry(1.6, 96, 48), new THREE.ShaderMaterial({
    uniforms: haloUniforms, vertexShader: VERT, fragmentShader: HALO_FRAG,
    side: THREE.BackSide, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
  }));
  halo.frustumCulled = false;
  group.add(halo);
  group.visible = false;
  return {
    group, uniforms,
    update(time, center, radius) {
      uniforms.uTime.value = time;
      group.position.copy(center);
      group.scale.setScalar(radius);
      haloUniforms.uCenter.value.copy(center);
      haloUniforms.uRadius.value = radius;
    },
  };
}
